import {
  AddUpdateTaskParams,
  dateFromDateString,
  toDateString,
  User,
} from '@nextforce/api'
import {
  CheckBox,
  Icon,
  ListItem,
  makeStyles,
  Text,
  useTheme,
} from '@rneui/themed'
import { t } from 'app/i18l'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { FlatList, TextInput, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { Collapsible } from 'shared/components/list/collapsible'
import { DateTimeChooser } from 'shared/components/list/date_time_chooser'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { RoundedButton } from 'shared/components/rounded-button'
import { TextRow } from 'shared/components/text_row'
import { DEFAULT_RECURRING_TASK_FIELDS } from 'tasks/context'
import { TaskViewItem } from 'tasks/screens/edit_task/items'
import { RecurrentTaskUpdateType } from 'tasks/types'
import { getRepeatText } from 'tasks/utils'

const MAX_DESCRIPTION_LENGTH = 280

interface Props {
  taskFields: AddUpdateTaskParams
  loading: boolean
  onSubmit(): Promise<void>
  onSetTaskFields(taskData: AddUpdateTaskParams): void
  onPressRecurring(): void
  users: User[] | undefined
  type: 'add' | 'edit'
  canSubmit: boolean
  updateType: RecurrentTaskUpdateType
  setUpdateType: (updateType: RecurrentTaskUpdateType) => void
  onPressAssignUser: () => void
}

export function EditTaskView({
  taskFields,
  onSetTaskFields,
  loading,
  onSubmit,
  onPressRecurring,
  users,
  canSubmit,
  type,
  updateType,
  setUpdateType,
  onPressAssignUser,
}: Props) {
  const styles = useStyles()
  const { theme } = useTheme()
  const minDueDate = moment().startOf('day').add(1, 'day').toDate()
  const selectedUser = useMemo(() => {
    return users?.find(user => user.id === taskFields.assignedTo)
  }, [users, taskFields.assignedTo])

  const items = useMemo(() => {
    const summaryItem: TaskViewItem = {
      type: 'summary',
      key: 'summary',
    }

    const descriptionItem: TaskViewItem = {
      type: 'description',
      key: 'description',
    }
    const dueDateItem: TaskViewItem = {
      type: 'dueDate',
      key: 'dueDate',
    }

    const assignedToItem: TaskViewItem = {
      type: 'assignedTo',
      key: 'assignedTo',
    }

    const photoRequiredItem: TaskViewItem = {
      type: 'photoRequired',
      key: 'photoRequired',
    }
    const recurringItem: TaskViewItem = {
      type: 'recurring',
      key: 'recurring',
    }
    const updateTypeItem: TaskViewItem = {
      type: 'updateType',
      key: 'updateType',
    }

    const listItems: TaskViewItem[] = [
      summaryItem,
      descriptionItem,
      dueDateItem,
      photoRequiredItem,
    ]
    if (type === 'edit') {
      listItems.push(updateTypeItem)
    } else {
      listItems.push(assignedToItem)
    }
    listItems.push(recurringItem)
    return listItems
  }, [type])

  const renderItem = useCallback(
    ({ item }: { item: TaskViewItem }) => {
      switch (item.type) {
        case 'summary':
          return (
            <View style={styles.summaryContainer}>
              <TextRow title={t('title')} />
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.textInput}
                  onChangeText={text => onSetTaskFields({ summary: text })}
                  value={taskFields.summary}
                  placeholder={t('taskSummaryPlaceholder')}
                  placeholderTextColor={theme.colors.grey2}
                />
              </View>
            </View>
          )
        case 'description':
          return (
            <View style={styles.descriptionContainer}>
              <TextRow title={t('description')} />
              <View style={styles.descriptionTextContainer}>
                <TextInput
                  style={[styles.textInput, styles.description]}
                  onChangeText={text => onSetTaskFields({ description: text })}
                  value={taskFields.description}
                  placeholder={t('taskDescriptionPlaceholder')}
                  multiline
                  maxLength={MAX_DESCRIPTION_LENGTH}
                  placeholderTextColor={theme.colors.grey2}
                />
              </View>
            </View>
          )
        case 'recurring':
          return (
            <RecurringItem
              onPressRecurring={onPressRecurring}
              taskFields={taskFields}
              onSetTaskFields={onSetTaskFields}
              disabled={type === 'edit'}
            />
          )
        case 'dueDate':
          return (
            <DateTimeChooser
              title={t('dueDate')}
              value={
                taskFields.dueDate
                  ? dateFromDateString(taskFields.dueDate)
                  : undefined
              }
              mode="date"
              onChange={(date: Date) =>
                onSetTaskFields({ dueDate: toDateString(date) })
              }
              minDate={minDueDate}
            />
          )
        case 'photoRequired':
          return (
            <CheckBox
              checked={!!taskFields.photoRequired}
              title={t('photoRequired')}
              onPress={() =>
                onSetTaskFields({
                  photoRequired: !taskFields.photoRequired,
                })
              }
              containerStyle={styles.photoRequiredContainer}
              textStyle={styles.photoRequiredText}
            />
          )
        case 'assignedTo':
          return (
            <View style={styles.assignedToContainer}>
              <ListItem onPress={onPressAssignUser}>
                <ListItem.Content>
                  <ListItem.Title style={styles.title}>
                    {t('assignTo')}
                  </ListItem.Title>
                </ListItem.Content>
                <Text style={styles.value}>
                  {selectedUser
                    ? `${selectedUser.firstName} ${selectedUser.lastName}`
                    : t('notAssigned')}
                </Text>
                <Icon
                  color={styles.icon.color}
                  style={styles.icon}
                  name="menu-right"
                  type="material-design"
                />
              </ListItem>
            </View>
          )
        case 'updateType':
          return (
            <UpdateTypeChooser value={updateType} setValue={setUpdateType} />
          )
        default:
          return null
      }
    },
    [
      styles.summaryContainer,
      styles.inputContainer,
      styles.textInput,
      styles.descriptionContainer,
      styles.descriptionTextContainer,
      styles.description,
      styles.photoRequiredContainer,
      styles.photoRequiredText,
      styles.assignedToContainer,
      styles.title,
      styles.value,
      styles.icon,
      taskFields,
      theme.colors.grey2,
      onPressRecurring,
      onSetTaskFields,
      type,
      minDueDate,
      onPressAssignUser,
      selectedUser,
      updateType,
      setUpdateType,
    ]
  )

  return (
    <>
      <FlatList style={styles.container} data={items} renderItem={renderItem} />
      <SafeAreaView edges={['left', 'right', 'bottom']}>
        <View style={styles.footer}>
          <RoundedButton
            onPress={onSubmit}
            danger
            title={type === 'edit' ? t('editTask') : t('createTask')}
            loading={loading}
            buttonStyle={styles.buttonStyle}
            disabled={!canSubmit}
          />
        </View>
      </SafeAreaView>
    </>
  )
}

interface RecurringItemProps {
  taskFields: AddUpdateTaskParams
  onSetTaskFields: (params: AddUpdateTaskParams) => void
  onPressRecurring: () => void
  disabled: boolean
}

export function RecurringItem({
  onPressRecurring,
  onSetTaskFields,
  taskFields,
  disabled,
}: RecurringItemProps) {
  const styles = useStyles()

  const onCancelRepeat = useCallback(() => {
    onSetTaskFields({
      ...taskFields,
      ...DEFAULT_RECURRING_TASK_FIELDS,
      recurring: false,
    })
  }, [onSetTaskFields, taskFields])

  if (taskFields.recurring) {
    const repeatText = getRepeatText(taskFields)

    return (
      <PressableOpacity onPress={onPressRecurring}>
        <View style={styles.recurringItemContainer}>
          <Icon name="repeat" />
          <View style={styles.recurringItemTextContainer}>
            <Text style={styles.recurringItemText}>{repeatText}</Text>
          </View>
          {disabled ? null : <Icon name="cancel" onPress={onCancelRepeat} />}
        </View>
      </PressableOpacity>
    )
  }

  return (
    <View style={styles.emptyRecurringItemContainer}>
      <ListItem onPress={disabled ? null : onPressRecurring}>
        <ListItem.Content>
          <ListItem.Title style={styles.title}>{t('repeat')}</ListItem.Title>
        </ListItem.Content>
        <Text style={styles.value}>{t('never')}</Text>
        <Icon
          color={styles.icon.color}
          style={styles.icon}
          name="menu-right"
          type="material-design"
        />
      </ListItem>
    </View>
  )
}

export function UpdateTypeChooser({
  title,
  value,
  setValue,
}: {
  title?: string
  value: RecurrentTaskUpdateType
  setValue: React.Dispatch<React.SetStateAction<RecurrentTaskUpdateType>>
}) {
  const UPDATE_TYPE_CHOOSER_ITEMS: {
    value: RecurrentTaskUpdateType
    label: string
  }[] = [
    {
      value: 'ALL',
      label: t('all'),
    },
    {
      value: 'THIS',
      label: t('this'),
    },
    {
      value: 'THIS_AND_FOLLOWING',
      label: t('thisAndFollowing'),
    },
  ]

  const styles = useStyles()
  const [expanded, setExpanded] = useState(!value)

  const onPressItem = useCallback(
    (item: RecurrentTaskUpdateType) => {
      setValue(item)
      setExpanded(false)
    },
    [setValue]
  )
  const valueLabel = UPDATE_TYPE_CHOOSER_ITEMS.find(
    item => item.value === value
  )?.label

  return (
    <Collapsible
      title={title || t('setUpdateType')}
      value={valueLabel}
      expanded={expanded}
      onPress={() => setExpanded(!expanded)}
      required={true}
    >
      {UPDATE_TYPE_CHOOSER_ITEMS.map((item, index) => (
        <ListItem
          key={index}
          onPress={() => onPressItem(item.value)}
          bottomDivider
        >
          <ListItem.Content>
            <ListItem.Title style={styles.option}>{item.label}</ListItem.Title>
          </ListItem.Content>
          <ListItem.CheckBox
            checked={item.value === value}
            checkedColor={styles.checkboxChecked.color}
            uncheckedColor={styles.checkboxUnchecked.color}
            checkedIcon="check"
            onPress={() => {
              onPressItem(item.value)
            }}
          />
        </ListItem>
      ))}
    </Collapsible>
  )
}

export const useStyles = makeStyles(theme => ({
  container: {
    backgroundColor: theme.colors?.white,
    flex: 1,
    marginTop: 10,
  },
  textInput: {
    marginTop: 10,
    color: theme.colors?.black,
    backgroundColor: theme.colors?.white,
    textAlignVertical: 'top',
    padding: 10,
    borderBottomWidth: 2,
    borderBottomColor: theme.colors?.grey1,
  },
  description: {
    height: 100,
    marginBottom: 15,
  },
  footer: {
    padding: 20,
  },
  descriptionTextContainer: {
    paddingHorizontal: 20,
    backgroundColor: theme.colors.white,
  },
  inputContainer: {
    paddingHorizontal: 20,
    backgroundColor: theme.colors?.white,
  },
  buttonStyle: {
    margin: 5,
  },
  photoRequiredContainer: {
    maxWidth: 300,
    marginVertical: 10,
  },
  photoRequiredText: {
    textAlign: 'justify',
    fontSize: 16,
    fontWeight: 'normal',
  },
  option: {
    marginHorizontal: 15,
    color: theme.colors?.grey2,
  },
  checkboxChecked: {
    color: theme.colors?.primary,
  },
  checkboxUnchecked: {
    color: theme.colors?.grey5,
  },
  title: {
    color: theme.colors?.black,
    marginHorizontal: 0,
  },
  value: {
    fontSize: 17,
    color: theme.colors?.grey2,
  },
  icon: {
    width: 24,
    color: theme.colors?.grey4,
  },
  summaryContainer: { paddingTop: 16 },
  descriptionContainer: { paddingTop: 16 },
  assignedToContainer: { marginVertical: 5 },
  recurringItemContainer: {
    padding: 10,
    borderColor: theme.colors.grey3,
    borderWidth: 1,
    borderRadius: 15,
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    margin: 10,
  },
  recurringItemTextContainer: { flex: 1 },
  recurringItemText: { marginLeft: 5 },
  emptyRecurringItemContainer: { marginVertical: 5 },
}))
